import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BookOpen, CheckCircle2, AlertCircle, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookVerification = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !orderNumber.trim()) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/book-verification/claim`, {
        email: email.trim(),
        order_number: orderNumber.trim()
      });
      
      if (response.data.status === "already_claimed") {
        toast.error("A claim already exists for this email");
      } else {
        setSubmitted(true);
        toast.success("Claim submitted successfully!");
      }
    } catch (error) {
      console.error("Error submitting claim:", error);
      toast.error("Failed to submit claim. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0D0F14] flex items-center justify-center p-6">
        <Card className="bg-[#1C2130] border-[rgba(255,255,255,0.07)] max-w-md w-full rounded-sm">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-[rgba(76,175,130,0.1)] flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-[#4CAF82]" />
            </div>
            <h1 
              className="text-2xl text-white mb-3"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Claim Submitted
            </h1>
            <p className="text-[#9BA8BE] text-[13px] font-light mb-6">
              We'll verify your purchase and activate your 6-month premium access. 
              You'll receive a confirmation email once approved.
            </p>
            <Button
              onClick={() => navigate("/login")}
              className="bg-[#C8860A] text-[#0D0F14] hover:bg-[#D4963A] uppercase text-[11px] tracking-wider px-6"
              style={{ fontFamily: 'DM Mono, monospace', borderRadius: '2px' }}
            >
              Return to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0F14] flex items-center justify-center p-6">
      <Card className="bg-[#1C2130] border-[rgba(255,255,255,0.07)] max-w-md w-full rounded-sm">
        <CardContent className="p-8">
          {/* Back button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-[#9BA8BE] hover:text-white text-[13px] mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-sm bg-[rgba(200,134,10,0.1)] flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-[#C8860A]" />
            </div>
            <div>
              <h1 
                className="text-xl text-white"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                I Have the Book
              </h1>
              <p className="text-[#9BA8BE] text-[12px]">Verify your purchase for 6 months free</p>
            </div>
          </div>

          {/* Info */}
          <div className="bg-[#232A3B] rounded-sm p-4 mb-6">
            <p className="text-[#9BA8BE] text-[13px] font-light">
              Owners of <em>ClarityOS: The 15-Minute Daily Protocol</em> receive 
              6 months of free premium access. Enter your email and order number 
              below to verify your purchase.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label 
                className="text-[#9BA8BE] text-[11px] uppercase tracking-wider"
                style={{ fontFamily: 'DM Mono, monospace' }}
              >
                Email Address
              </Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                className="bg-[#232A3B] border-[rgba(255,255,255,0.07)] text-white placeholder:text-slate-600 text-[13px] h-11 rounded-sm"
                required
              />
            </div>

            <div className="space-y-2">
              <Label 
                className="text-[#9BA8BE] text-[11px] uppercase tracking-wider"
                style={{ fontFamily: 'DM Mono, monospace' }}
              >
                Order Number
              </Label>
              <Input
                type="text"
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                placeholder="e.g., AMZ-123-4567890"
                className="bg-[#232A3B] border-[rgba(255,255,255,0.07)] text-white placeholder:text-slate-600 text-[13px] h-11 rounded-sm"
                required
              />
              <p className="text-slate-500 text-[11px]">
                Find this in your order confirmation email from Amazon, your bookstore, or publisher.
              </p>
            </div>

            <Button
              type="submit"
              disabled={submitting || !email.trim() || !orderNumber.trim()}
              className="w-full bg-[#C8860A] text-[#0D0F14] hover:bg-[#D4963A] uppercase text-[11px] tracking-wider py-3"
              style={{ fontFamily: 'DM Mono, monospace', borderRadius: '2px' }}
            >
              {submitting ? "Submitting..." : "Submit for Verification"}
            </Button>
          </form>

          {/* Note */}
          <p className="text-slate-500 text-[11px] text-center mt-6">
            Verification typically takes 1-2 business days. We'll email you once approved.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookVerification;
